/**
 * /phone-health — how the three Twilio lines are doing (Oct 27, 2026).
 *
 * Greg: "do we have tracking of the twilio number, routing, pickups,
 * failures...so we can maintain". One page for: calls into the customer line
 * (answered, missed, voicemails, who it rang), outgoing calls by rep, texts by
 * line with failures, Twilio's own error alerts, and where each number's
 * calls and texts are routed.
 *
 * Data: phone_health_report(from, to) (supabase/migrations/20261027_phone_health.sql).
 * Rules (answered = completed and over 30 s, or a person picked up, stuck, no receipt) are in
 * lib/phoneHealth.ts. Leadership only: admin, sales_director,
 * director_of_operations; the RPC refuses everyone else.
 */
import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import DealLink from "../components/DealLink";
import { useTabParam } from "../lib/useTabParam";
import { formatPhone } from "../lib/phoneFormat";
import {
  RANGES, LINE_LABEL, ago, codeLink, codeMeaning, fetchPhoneHealth, fnFromUrl, healthFlags, pct, rangeStart,
  type Outcome, type PhoneHealthReport, type RangeKey,
} from "../lib/phoneHealth";

const RANGE_KEYS = RANGES.map((r) => r.key);

function fmtTime(iso: string): string {
  return new Date(iso).toLocaleString("en-US", {
    month: "short", day: "numeric", hour: "numeric", minute: "2-digit", timeZone: "America/New_York",
  });
}

function fmtDay(day: string): string {
  return new Date(`${day}T12:00:00`).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
}

function Tile({ label, value, sub, tone = "slate" }: { label: string; value: string | number; sub?: string; tone?: "slate" | "emerald" | "rose" | "amber" }) {
  const color = { slate: "text-slate-900", emerald: "text-emerald-700", rose: "text-rose-700", amber: "text-amber-700" }[tone];
  return (
    <div className="rounded-lg border border-slate-200 bg-white px-3 py-2">
      <div className="text-[11px] uppercase tracking-wide text-slate-500">{label}</div>
      <div className={`text-xl font-bold ${color}`}>{value}</div>
      {sub && <div className="text-[11px] text-slate-500">{sub}</div>}
    </div>
  );
}

function Section({ title, sub, children }: { title: string; sub?: string; children: ReactNode }) {
  return (
    <section className="rounded-xl border border-slate-200 bg-white p-4 space-y-3">
      <div>
        <h2 className="text-sm font-bold text-slate-900">{title}</h2>
        {sub && <p className="text-xs text-slate-500">{sub}</p>}
      </div>
      {children}
    </section>
  );
}

const OUTCOME_CHIP: Record<Outcome, { label: string; cls: string }> = {
  answered: { label: "Answered", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  missed: { label: "Missed", cls: "bg-rose-50 text-rose-700 border-rose-200" },
  in_progress: { label: "In progress", cls: "bg-sky-50 text-sky-700 border-sky-200" },
  stuck: { label: "Never finished", cls: "bg-rose-100 text-rose-800 border-rose-300" },
};

function CodeCell({ code }: { code: string | null }) {
  if (!code) return <span className="text-slate-400">—</span>;
  const meaning = codeMeaning(code);
  const link = codeLink(code);
  return (
    <span>
      {link ? (
        <a href={link} target="_blank" rel="noreferrer" className="font-mono text-bardin-teal hover:underline">{code}</a>
      ) : (
        <span className="font-mono">{code}</span>
      )}
      {meaning && <span className="text-slate-600"> · {meaning}</span>}
    </span>
  );
}

export default function PhoneHealth() {
  const [range, setRange] = useTabParam<RangeKey>("range", "7d", RANGE_KEYS);
  const [report, setReport] = useState<PhoneHealthReport | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [showAllCalls, setShowAllCalls] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const now = new Date();
      setReport(await fetchPhoneHealth(rangeStart(range, now), now));
      setError(null);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, [range]);

  useEffect(() => { void load(); }, [load]);

  // Refresh every minute while the tab is visible.
  useEffect(() => {
    const t = window.setInterval(() => { if (document.visibilityState === "visible") void load(); }, 60_000);
    return () => window.clearInterval(t);
  }, [load]);

  const flags = useMemo(() => (report ? healthFlags(report) : []), [report]);
  const maxDay = useMemo(() => Math.max(1, ...(report?.inbound.by_day ?? []).map((d) => d.total)), [report]);

  const ib = report?.inbound;
  const ob = report?.outbound;
  const recent = ib ? (showAllCalls ? ib.recent : ib.recent.slice(0, 8)) : [];

  return (
    <div className="max-w-5xl mx-auto px-4 py-4 space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-lg font-bold text-slate-900">📞 Phone Health</h1>
          <p className="text-sm text-slate-500">
            Calls, texts and Twilio errors on the three Bardin lines. <Link to="/admin" className="text-bardin-teal hover:underline">Admin</Link>
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5">
            {RANGES.map((r) => (
              <button
                key={r.key}
                onClick={() => setRange(r.key)}
                className={`px-3 py-1 text-xs font-semibold rounded-md ${range === r.key ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-50"}`}
              >
                {r.label}
              </button>
            ))}
          </div>
          <button onClick={() => void load()} disabled={loading} className="text-xs px-2 py-1 rounded-md border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 disabled:opacity-50">
            {loading ? "…" : "Refresh"}
          </button>
        </div>
      </div>

      {error && <div className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-800">Couldn't load phone health: {error}</div>}
      {!report && !error && <div className="text-sm text-slate-500">Loading…</div>}

      {report && ib && ob && (
        <>
          {/* What needs attention */}
          <section className={`rounded-xl border p-4 ${flags.some((f) => f.tone === "rose") ? "border-rose-200 bg-rose-50/40" : flags.length ? "border-amber-200 bg-amber-50/40" : "border-emerald-200 bg-emerald-50/40"}`}>
            <h2 className="text-sm font-bold text-slate-900">{flags.length ? "Needs a look" : "✓ All clear"}</h2>
            {flags.length === 0 ? (
              <p className="text-sm text-slate-600">No stuck calls, routing changes, webhook errors or failed texts in this window.</p>
            ) : (
              <ul className="mt-1 space-y-1">
                {flags.map((f, i) => (
                  <li key={i} className="text-sm flex gap-2">
                    <span className={f.tone === "rose" ? "text-rose-600" : f.tone === "amber" ? "text-amber-600" : "text-slate-400"}>●</span>
                    <span className="text-slate-800">{f.text}</span>
                  </li>
                ))}
              </ul>
            )}
            <p className="mt-2 text-[11px] text-slate-500">
              Twilio errors and number settings last copied {ago(report.synced.alerts_at)} (hourly). Page refreshed {ago(report.generated_at)}.
            </p>
          </section>

          {/* Incoming calls */}
          <Section
            title="Calls in — customer line (470) 823-8816"
            sub={`Homeowners and unknown callers. A call counts as answered when someone stayed on more than 30 seconds, or Twilio heard a person pick up. ${ib.staff_calls} staff call${ib.staff_calls === 1 ? "" : "s"} and ${ib.line_tests} line test${ib.line_tests === 1 ? "" : "s"} left out.`}
          >
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
              <Tile label="Calls in" value={ib.outside_total} sub={`${ib.known_callers} known · ${ib.unknown_callers} unknown`} />
              <Tile label="Answered" value={ib.answered} sub={pct(ib.answered, ib.outside_total) != null ? `${pct(ib.answered, ib.outside_total)}% picked up` : undefined} tone="emerald" />
              <Tile label="Missed" value={ib.missed} tone={ib.missed ? "rose" : "slate"} />
              <Tile label="Voicemails" value={ib.voicemails} />
              <Tile label="Missed, no voicemail" value={ib.missed_no_vm} tone={ib.missed_no_vm ? "amber" : "slate"} />
              <Tile label="Texted back" value={ib.texted_back} sub="missed-call text" />
            </div>

            {ib.by_day.length > 1 && (
              <div className="space-y-1">
                {ib.by_day.map((d) => (
                  <div key={d.day} className="flex items-center gap-2 text-xs">
                    <div className="w-24 shrink-0 text-slate-500">{fmtDay(d.day)}</div>
                    <div className="flex-1 flex h-4 rounded bg-slate-100 overflow-hidden" style={{ maxWidth: `${(d.total / maxDay) * 100}%`, minWidth: "8px" }}>
                      <div className="bg-emerald-500" style={{ width: `${(d.answered / Math.max(1, d.total)) * 100}%` }} />
                      <div className="bg-rose-400" style={{ width: `${(d.missed / Math.max(1, d.total)) * 100}%` }} />
                    </div>
                    <div className="w-20 shrink-0 text-slate-600">{d.answered}/{d.total} answered</div>
                  </div>
                ))}
              </div>
            )}

            {ib.by_person.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-left text-slate-500 border-b border-slate-200">
                      <th className="py-1 pr-2 font-semibold">Whose phone rang</th>
                      <th className="py-1 px-2 font-semibold text-right">Rang</th>
                      <th className="py-1 px-2 font-semibold text-right">Answered</th>
                      <th className="py-1 px-2 font-semibold text-right">Missed</th>
                      <th className="py-1 pl-2 font-semibold text-right">Pickup</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ib.by_person.map((p) => {
                      const rate = pct(p.answered, p.rung);
                      return (
                        <tr key={p.member_id ?? p.name} className="border-b border-slate-100">
                          <td className="py-1 pr-2 text-slate-800">{p.name}</td>
                          <td className="py-1 px-2 text-right">{p.rung}</td>
                          <td className="py-1 px-2 text-right text-emerald-700">{p.answered}</td>
                          <td className="py-1 px-2 text-right text-rose-700">{p.missed}</td>
                          <td className={`py-1 pl-2 text-right font-semibold ${rate == null ? "text-slate-400" : rate >= 60 ? "text-emerald-700" : rate >= 35 ? "text-amber-700" : "text-rose-700"}`}>{rate == null ? "—" : `${rate}%`}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                <p className="mt-1 text-[11px] text-slate-500">When a call rings two people (a rep and their split, or a rep's cell and laptop), it counts for each.</p>
              </div>
            )}

            {ib.stuck_calls.length > 0 && (
              <div className="rounded-lg border border-rose-200 bg-rose-50 p-3">
                <div className="text-xs font-bold text-rose-800">Calls that never finished</div>
                <p className="text-[11px] text-rose-700 mb-1">Still marked "{ib.stuck_calls[0].status}" an hour later: Twilio's last step for the call didn't reach the CRM. This is how the Sep–Oct hang-ups first showed.</p>
                {ib.stuck_calls.map((c, i) => (
                  <div key={i} className="text-xs text-rose-900">
                    {fmtTime(c.at)} · {formatPhone(c.from)} · {c.status}{c.deal_id ? <> · <DealLink id={c.deal_id}>deal</DealLink></> : null}
                  </div>
                ))}
              </div>
            )}

            {recent.length > 0 && (
              <div>
                <div className="text-xs font-semibold text-slate-700 mb-1">Recent calls</div>
                <div className="divide-y divide-slate-100 rounded-lg border border-slate-200">
                  {recent.map((c, i) => {
                    const chip = OUTCOME_CHIP[c.outcome];
                    return (
                      <div key={i} className="flex flex-wrap items-center gap-x-2 gap-y-0.5 px-3 py-1.5 text-xs">
                        <span className="w-28 shrink-0 text-slate-500">{fmtTime(c.at)}</span>
                        <span className="font-medium text-slate-800">
                          {c.deal_id ? <DealLink id={c.deal_id}>{c.homeowner || formatPhone(c.from)}</DealLink> : formatPhone(c.from)}
                        </span>
                        <span className={`rounded border px-1.5 py-0.5 text-[10px] font-semibold ${chip.cls}`}>{chip.label}</span>
                        {c.duration ? <span className="text-slate-500">{c.duration}s</span> : null}
                        {c.left_vm && <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-700">voicemail</span>}
                        {c.texted_back && <span className="rounded bg-sky-50 px-1.5 py-0.5 text-[10px] text-sky-700">texted back</span>}
                        {c.rang && <span className="text-slate-500">rang {c.rang}</span>}
                      </div>
                    );
                  })}
                </div>
                {ib.recent.length > 8 && (
                  <button onClick={() => setShowAllCalls((v) => !v)} className="mt-1 text-xs text-bardin-teal hover:underline">
                    {showAllCalls ? "Show fewer" : `Show all ${ib.recent.length}`}
                  </button>
                )}
              </div>
            )}
          </Section>

          {/* Outgoing calls */}
          <Section title="Calls out — by rep" sub="Calls placed from the CRM (laptop softphone or the call-my-cell bridge). Talked = connected more than 30 seconds.">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <Tile label="Calls out" value={ob.total} />
              <Tile label="Talked 30s+" value={ob.talked} sub={pct(ob.talked, ob.total) != null ? `${pct(ob.talked, ob.total)}% of calls` : undefined} tone="emerald" />
              <Tile label="No answer / busy" value={ob.no_answer} />
              <Tile label="Failed" value={ob.failed} tone={ob.failed ? "rose" : "slate"} />
            </div>
            {ob.by_rep.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-left text-slate-500 border-b border-slate-200">
                      <th className="py-1 pr-2 font-semibold">Rep</th>
                      <th className="py-1 px-2 font-semibold text-right">Calls</th>
                      <th className="py-1 px-2 font-semibold text-right">Talked 30s+</th>
                      <th className="py-1 px-2 font-semibold text-right">Short</th>
                      <th className="py-1 px-2 font-semibold text-right">No answer</th>
                      <th className="py-1 px-2 font-semibold text-right">Failed</th>
                      <th className="py-1 pl-2 font-semibold text-right">Talk min</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ob.by_rep.map((r) => (
                      <tr key={r.member_id ?? r.name} className="border-b border-slate-100">
                        <td className="py-1 pr-2 text-slate-800">{r.name}</td>
                        <td className="py-1 px-2 text-right">{r.calls}</td>
                        <td className="py-1 px-2 text-right text-emerald-700">{r.talked}{pct(r.talked, r.calls) != null ? <span className="text-slate-400"> ({pct(r.talked, r.calls)}%)</span> : null}</td>
                        <td className="py-1 px-2 text-right">{r.short}</td>
                        <td className="py-1 px-2 text-right">{r.no_answer}</td>
                        <td className={`py-1 px-2 text-right ${r.failed ? "text-rose-700" : ""}`}>{r.failed}</td>
                        <td className="py-1 pl-2 text-right">{r.talk_minutes}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Section>

          {/* Texts */}
          <Section title="Texts — by line" sub="No receipt = Twilio never told us whether it was delivered (an hour or more later).">
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-left text-slate-500 border-b border-slate-200">
                    <th className="py-1 pr-2 font-semibold">Line</th>
                    <th className="py-1 px-2 font-semibold text-right">Sent</th>
                    <th className="py-1 px-2 font-semibold text-right">Delivered</th>
                    <th className="py-1 px-2 font-semibold text-right">Failed</th>
                    <th className="py-1 px-2 font-semibold text-right">No receipt</th>
                    <th className="py-1 px-2 font-semibold text-right">Received</th>
                    <th className="py-1 pl-2 font-semibold">Why they failed</th>
                  </tr>
                </thead>
                <tbody>
                  {report.texts.map((t) => (
                    <tr key={t.line} className="border-b border-slate-100 align-top">
                      <td className="py-1 pr-2 text-slate-800 whitespace-nowrap">{LINE_LABEL[t.line] ?? t.line}</td>
                      <td className="py-1 px-2 text-right">{t.sent}</td>
                      <td className="py-1 px-2 text-right text-emerald-700">{t.delivered}{pct(t.delivered, t.sent) != null ? <span className="text-slate-400"> ({pct(t.delivered, t.sent)}%)</span> : null}</td>
                      <td className={`py-1 px-2 text-right ${t.failed ? "text-rose-700 font-semibold" : ""}`}>{t.failed}</td>
                      <td className={`py-1 px-2 text-right ${t.no_receipt ? "text-amber-700" : ""}`}>{t.no_receipt}</td>
                      <td className="py-1 px-2 text-right">{t.received}</td>
                      <td className="py-1 pl-2 space-y-0.5">
                        {t.codes.length === 0 ? <span className="text-slate-400">—</span> : t.codes.map((c) => (
                          <div key={c.code}>{c.n} × <CodeCell code={c.code} /></div>
                        ))}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {report.failed_texts.length > 0 && (
              <details>
                <summary className="cursor-pointer text-xs font-semibold text-slate-700">Failed or unconfirmed texts ({report.failed_texts.length})</summary>
                <div className="mt-2 divide-y divide-slate-100 rounded-lg border border-slate-200">
                  {report.failed_texts.map((f, i) => (
                    <div key={i} className="px-3 py-1.5 text-xs flex flex-wrap gap-x-2">
                      <span className="w-28 shrink-0 text-slate-500">{fmtTime(f.at)}</span>
                      <span className="text-slate-800 font-medium">{f.who || formatPhone(f.to)}</span>
                      <span className="text-slate-500">{f.line} line{f.tag ? ` · ${f.tag}` : ""}</span>
                      {f.code ? <CodeCell code={f.code} /> : <span className="text-amber-700">no receipt ({f.status ?? "no status"})</span>}
                    </div>
                  ))}
                </div>
              </details>
            )}
          </Section>

          {/* Twilio alerts */}
          <Section title="Twilio errors" sub="Twilio's own error log, copied in hourly. Error codes link to Twilio's explanation.">
            {report.alerts.total === 0 ? (
              <p className="text-sm text-slate-600">No Twilio errors in this window.</p>
            ) : (
              <>
                <div className="space-y-1">
                  {report.alerts.by_code.map((c) => (
                    <div key={c.code ?? "none"} className="text-xs flex flex-wrap gap-x-2">
                      <span className="w-8 text-right font-semibold text-slate-800">{c.n}×</span>
                      <CodeCell code={c.code} />
                      <span className="text-slate-400">last {ago(c.last)}</span>
                    </div>
                  ))}
                </div>
                <details>
                  <summary className="cursor-pointer text-xs font-semibold text-slate-700">Each error ({report.alerts.recent.length})</summary>
                  <div className="mt-2 divide-y divide-slate-100 rounded-lg border border-slate-200">
                    {report.alerts.recent.map((a, i) => (
                      <div key={i} className="px-3 py-1.5 text-xs flex flex-wrap gap-x-2">
                        <span className="w-28 shrink-0 text-slate-500">{fmtTime(a.at)}</span>
                        <CodeCell code={a.code} />
                        {(a.who || a.to) && <span className="text-slate-700">to {a.who || formatPhone(a.to)}</span>}
                        {a.tag && <span className="text-slate-500">{a.tag}</span>}
                        {a.url && <span className="text-slate-500 break-all">{fnFromUrl(a.url)}</span>}
                      </div>
                    ))}
                  </div>
                </details>
              </>
            )}
          </Section>

          {/* Routing */}
          <Section title="Routing" sub="Where Twilio sends each number's calls and texts, and the switches that change call handling.">
            <div className="space-y-2">
              {report.numbers.length === 0 && <p className="text-sm text-slate-600">Not copied from Twilio yet.</p>}
              {report.numbers.map((n) => {
                const v = fnFromUrl(n.voice_url);
                const s = fnFromUrl(n.sms_url);
                const vOk = !n.expected_voice || v === n.expected_voice;
                const sOk = !n.expected_sms || s === n.expected_sms;
                return (
                  <div key={n.number} className="rounded-lg border border-slate-200 px-3 py-2 text-xs">
                    <div className="font-semibold text-slate-900">{n.name ?? formatPhone(n.number)}</div>
                    <div className={vOk ? "text-slate-600" : "text-rose-700 font-semibold"}>{vOk ? "✓" : "⚠"} Calls → {v ?? "nothing set"}{!vOk && n.expected_voice ? ` (expected ${n.expected_voice})` : ""}</div>
                    <div className={sOk ? "text-slate-600" : "text-rose-700 font-semibold"}>{sOk ? "✓" : "⚠"} Texts → {s ?? "nothing set"}{!sOk && n.expected_sms ? ` (expected ${n.expected_sms})` : ""}</div>
                  </div>
                );
              })}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
              {[
                ["call_menu_mode", "Menu for unknown callers", "on"],
                ["call_forward_amd", "Voicemail detection on reps' cells", "on"],
                ["missed_call_textback_mode", "Missed-call text to the caller", "on"],
                ["missed_call_owner_sms", "Missed-call alert to the rep", "on"],
              ].map(([key, label, normal]) => {
                const raw = report.settings[key];
                const val = raw == null ? `${normal} (not set)` : String(raw);
                const off = raw != null && String(raw) !== normal && String(raw) !== "true";
                return (
                  <div key={key} className="rounded-lg border border-slate-200 px-3 py-2 flex justify-between gap-2">
                    <span className="text-slate-700">{label}</span>
                    <span className={`font-mono ${off ? "text-amber-700 font-semibold" : "text-slate-500"}`}>{val}</span>
                  </div>
                );
              })}
            </div>
          </Section>
        </>
      )}
    </div>
  );
}
